/**
 * gameAudio/cue.ts — UI cue bus (tap / primary / success / danger / disabled /
 * scene / nav / message). PR-16 split from lib/gameAudio.ts.
 *
 * Each cue first tries to play a small recorded .ogg from /sounds/ui/* (loaded
 * lazily on the first cue of that kind), then falls back to inline oscillator
 * synthesis so a missing/late file never blocks a UI beep. classifyCue maps
 * a pointerdown EventTarget to the right cue kind based on which control was
 * tapped — this is what AppShell wires to its global pointerdown listener so
 * every interactive element gets a consistent acoustic feedback.
 *
 * 调用关系：
 *   - AppShell + AppShellV2 call classifyCue(e.target) + playCue(kind) on
 *     pointerdown; preloadUiSounds() is invoked after the first user gesture.
 *   - HomePage, ChatPage, QuizPage and most pages call playCue directly for
 *     reward / pop / nav feedback that doesn't tie to a DOM control.
 *   - Bus + synth helpers come from ./_context (shared AudioContext).
 *
 * 关键约束：
 *   - Volume is bus-scaled in _context (SFX_GAIN); per-cue env.gain.value here
 *     is the *local* mix relative to that bus. Reducing here without
 *     re-balancing other buses will make UI feel quiet.
 *   - playCue is fire-and-forget; never returns a promise. Callers MUST NOT
 *     await it (would queue inside React event handlers and cause click lag).
 */

import {
  arpeggio,
  ensureAudio,
  getAudioEnabled,
  getSfxGain,
  loadSample,
  noiseTick,
  sampleBufferCache,
  tone,
} from './_context';

export type CueKind = 'tap' | 'primary' | 'success' | 'danger' | 'disabled' | 'scene' | 'nav' | 'message';

// Paths under frontend/public/sounds/ui/ — preloaded after the first user
// gesture by preloadUiSounds(). Each cue plays a recorded .ogg if cached;
// otherwise playCue() falls through to the synthesized variant below so a
// missing/late file never blocks a UI beep.
const UI_SAMPLE_PATHS: Record<CueKind, string> = {
  tap: '/sounds/ui/click_soft.ogg',
  primary: '/sounds/ui/select_soft.ogg',
  success: '/sounds/ui/confirm_soft.ogg',
  danger: '/sounds/ui/error_soft.ogg',
  disabled: '/sounds/ui/error_soft.ogg',
  scene: '/sounds/ui/open_soft.ogg',
  nav: '/sounds/ui/select_soft.ogg',
  message: '/sounds/ui/question_soft.ogg',
};

export function playCue(kind: CueKind) {
  if (!getAudioEnabled()) return;
  const audio = ensureAudio();
  const sfx = getSfxGain();
  if (!audio || !sfx) return;
  if (audio.state === 'suspended') void audio.resume();
  if (playUiSample(kind)) return;

  if (kind === 'disabled') {
    tone(150, 0.045, 'triangle', 0.05);
    tone(95, 0.07, 'sine', 0.035, 0.035);
    return;
  }
  if (kind === 'success') {
    arpeggio([523.25, 659.25, 783.99], 0.045, 0.05);
    return;
  }
  if (kind === 'message') {
    arpeggio([659.25, 880, 1174.66, 880], 0.055, 0.075, 0.038);
    tone(523.25, 0.12, 'triangle', 0.026, 0.32);
    return;
  }
  if (kind === 'danger') {
    tone(220, 0.055, 'sawtooth', 0.035);
    tone(164.81, 0.075, 'triangle', 0.03, 0.045);
    return;
  }
  if (kind === 'scene') {
    arpeggio([880, 1174.66], 0.038, 0.032, 0.028);
    noiseTick(0.045, 0.018);
    return;
  }
  if (kind === 'primary') {
    tone(440, 0.035, 'triangle', 0.035);
    tone(659.25, 0.055, 'sine', 0.028, 0.025);
    return;
  }
  if (kind === 'nav') {
    tone(330, 0.032, 'triangle', 0.026);
    return;
  }
  tone(310, 0.028, 'triangle', 0.025);
  tone(470, 0.025, 'sine', 0.016, 0.018);
}

export function preloadUiSounds() {
  if (!getAudioEnabled()) return;
  for (const path of Object.values(UI_SAMPLE_PATHS)) void loadSample(path);
}

function playUiSample(kind: CueKind): boolean {
  const audio = ensureAudio();
  const sfx = getSfxGain();
  if (!audio || !sfx) return false;
  const path = UI_SAMPLE_PATHS[kind];
  const buf = sampleBufferCache.get(path);
  if (!buf) {
    void loadSample(path);
    return false;
  }
  const src = audio.createBufferSource();
  const env = audio.createGain();
  src.buffer = buf;
  env.gain.value = kind === 'danger' || kind === 'disabled' ? 0.38 : 0.46;
  src.connect(env);
  env.connect(sfx);
  src.start();
  return true;
}

/**
 * Walk up from a pointerdown target to the nearest interactive control and
 * return the cue kind to play. Returns null for non-control targets so the
 * AppShell can stay silent on background taps.
 *
 * Order matters: disabled wins over visual style, nav wins over generic tap,
 * btn-danger wins over btn-primary so destructive actions feel different.
 */
export function classifyCue(target: EventTarget | null): CueKind | null {
  if (!(target instanceof Element)) return null;
  const control = target.closest('button, a, [role="button"], input, textarea, select');
  if (!control) {
    if (target.closest('.home-scene-wrap, canvas')) return 'scene';
    return null;
  }
  const disabled = control instanceof HTMLButtonElement
    ? control.disabled
    : control.getAttribute('aria-disabled') === 'true';
  if (disabled) return 'disabled';
  if (control.closest('.bottom-nav, .app-mobile-header')) return 'nav';
  if (control.matches('.btn-danger') || control.closest('.btn-danger')) return 'danger';
  if (control.matches('.btn-success, .home-mission-claim.ready') || control.closest('.btn-success, .home-mission-claim.ready')) return 'success';
  if (control.matches('.btn-primary, .home-primary-action, .home-stage-challenge-btn') || control.closest('.btn-primary, .home-primary-action, .home-stage-challenge-btn')) return 'primary';
  return 'tap';
}
